import { createPortal } from 'react-dom'
import { Check, ChevronDown, Copy, Phone } from 'lucide-react'
import { QuoSmsButton } from './QuoSmsButton'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'

/** One contact entry inside the popover: label (driver name / "Emergency contact"), the number, and a
 *  Call / Quo / copy action row. Quo opens the workspace line with `quoText` prefilled.
 *
 *  There is no `sms:` link beside it any more. That handed the number to the dispatcher's OWN device,
 *  so the message left from their personal number: the driver saw a stranger, the thread never had
 *  it, and nothing was recorded against the company line — which is the whole reason the SMS surface
 *  exists. The Quo button is the one that writes from the line the driver already knows. */
interface ContactEntryProps {
    label: string
    phone: string
    clipboardLabel?: string
    quoText?: string
}

const ContactEntry = ({ label, phone, clipboardLabel = label, quoText }: ContactEntryProps) => {
    // Shared hook: confirmation timer cleared on unmount, refused clipboard surfaces an error toast
    // instead of a false checkmark.
    const { copiedKey: copied, copy } = useCopyToClipboard()
    return (
        <div className="px-2.5 py-2">
            <div className="text-xs font-bold text-foreground">{label}</div>
            <div className="mt-0.5 text-xs text-foreground-muted">{phone}</div>
            <div className="mt-1.5 flex items-center gap-1.5">
                <a
                    href={`tel:${phone}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-success-soft px-2.5 py-1 text-xs font-semibold text-success transition-opacity hover:opacity-80"
                >
                    <Phone size={12} /> Call
                </a>
                <QuoSmsButton phone={phone} text={quoText} />
                <button
                    type="button"
                    onClick={() => void copy(`${clipboardLabel}\n${phone}`)}
                    title="Copy name and number"
                    aria-label={`Copy ${clipboardLabel} name and number`}
                    className="inline-flex items-center justify-center rounded-lg border border-border bg-background p-1.5 text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
                >
                    {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                </button>
            </div>
        </div>
    )
}

/**
 * Driver-cell contact dropdown (atrek-style): a chevron next to the name opens an inline popover with the
 * driver's phone and emergency contact — each with Call / SMS / copy. Replaces the table's Phone column.
 */
export const DriverContactPopover = ({
    name,
    phone,
    emergencyPhone,
    quoText,
}: {
    name: string
    phone: string | null
    emergencyPhone: string | null
    /** Prefill for the Quo deep-link — the load nudge, or the team's recruiting pitch. */
    quoText?: string
}) => {
    // Portaled + anchored (same hook as DriverLocationEditor in the same table): an absolute panel would be
    // clipped by the drivers table's overflow-x-auto wrapper for rows near the bottom edge.
    const { open, anchorRect, containerRef, triggerRef, panelRef, togglePanel, panelPositionStyle } = useAnchoredPopover()
    if (!phone && !emergencyPhone) return null
    return (
        <div ref={containerRef} className="inline-flex" onClick={(e) => e.stopPropagation()}>
            <button
                ref={triggerRef}
                type="button"
                onClick={togglePanel}
                title="Contact numbers"
                aria-label={`Contact numbers for ${name}`}
                className="flex size-5 items-center justify-center rounded text-foreground-muted/70 transition-colors hover:bg-default hover:text-foreground"
            >
                <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={panelRef}
                        // The trigger is a tiny chevron — ignore the hook's trigger-width sizing.
                        style={{ ...panelPositionStyle, width: 224 }}
                        className="divide-y divide-border rounded-xl border border-border bg-surface py-0.5 shadow-overlay"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {phone && <ContactEntry label={name} phone={phone} quoText={quoText} />}
                        {emergencyPhone && (
                            <ContactEntry label="Emergency contact" clipboardLabel={`${name} — Emergency contact`} phone={emergencyPhone} quoText={quoText} />
                        )}
                    </div>,
                    document.body
                )}
        </div>
    )
}
